import React, { useState, useEffect } from 'react';
import api from '../../api';
import './NoticesPage.css';

export default function NoticesPage({ onNavigate }) {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNotices = async () => {
      try {
        const res = await api.get('/notices.php');
        const data = Array.isArray(res.data) ? res.data : [];
        // Sort newest first
        data.sort((a, b) => new Date(b.date) - new Date(a.date));
        setNotices(data);
      } catch (err) {
        console.error("Failed to load notices:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchNotices();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') || url.startsWith('/') 
      ? url 
      : `${api.defaults.baseURL}/${url}`;
  };

  return (
    <div className="notices-page-dedicated">
      {/* ── Page Header & Breadcrumbs ── */}
      <header className="notices-page__header">
        <div className="notices-page__header-overlay" />
        <div className="notices-page__header-content">
          <nav className="notices-page__breadcrumb" aria-label="Breadcrumb">
            <span className="notices-page__breadcrumb-item" onClick={() => onNavigate?.('home')}>
              Home
            </span>
            <span className="notices-page__breadcrumb-separator">/</span>
            <span className="notices-page__breadcrumb-item notices-page__breadcrumb-item--active">
              Notices Board
            </span>
          </nav>
          <h1 className="notices-page__title">Library Announcements & Notices</h1>
          <p className="notices-page__subtitle">
            Stay updated with the latest circulars, alerts, events, and timings from our library.
          </p>
        </div>
      </header>

      {/* ── Main Grid Content ── */}
      <main className="notices-page__main">
        {loading ? (
          <div className="notices-page__loading">
            <i className="fa-solid fa-spinner fa-spin notices-page__spinner"></i>
            <p>Loading notices...</p>
          </div>
        ) : notices.length === 0 ? (
          <div className="notices-page__empty">
            <i className="fa-regular fa-bell-slash notices-page__empty-icon"></i>
            <h3>No Active Notices</h3>
            <p>There are no announcements currently published on the notices board.</p>
            <button className="notices-page__back-btn" onClick={() => onNavigate?.('home')}>
              Back to Home
            </button>
          </div>
        ) : (
          <div className="notices-page__grid">
            {notices.map((notice, idx) => {
              const categoryClass = (notice.category || '').toLowerCase();
              const categoryIcon = notice.categoryIcon || 'fa-solid fa-bell';
              const tagIcon = notice.tagIcon || 'fa-solid fa-tag';
              const fileUrl = getFullUrl(notice.image_url);

              return (
                <article key={notice.id || idx} className="notice-card-dedicated">
                  {/* Card Media Preview */}
                  {notice.image_url && (
                    <div className="notice-card-dedicated__media">
                      <img src={fileUrl} alt={notice.title} className="notice-card-dedicated__img" />
                      <div className="notice-card-dedicated__media-overlay" />
                    </div>
                  )}

                  {/* Top bar accent */}
                  <div className="notice-card-dedicated__bar" />

                  {/* Header Row */}
                  <div className="notice-card-dedicated__head">
                    <span className={`notice-card-dedicated__category notice-card-dedicated__category--${categoryClass}`}>
                      <i className={categoryIcon}></i>
                      {notice.category}
                    </span>
                    {parseInt(notice.isNew) === 1 && (
                      <span className="notice-card-dedicated__new">
                        <i className="fa-solid fa-circle" style={{ fontSize: 6 }}></i>
                        New
                      </span>
                    )}
                  </div>

                  {/* Body Content */}
                  <div className="notice-card-dedicated__body">
                    {/* Date */}
                    <div className="notice-card-dedicated__date">
                      <i className="fa-regular fa-calendar-days"></i>
                      {formatDate(notice.date)}
                    </div>
                    {/* Title */}
                    <h3 className="notice-card-dedicated__title" title={notice.title}>
                      {notice.title}
                    </h3>
                  </div>

                  {/* Footer Row */}
                  <div className="notice-card-dedicated__footer" style={{ marginTop: 'auto' }}>
                    <span className="notice-card-dedicated__tag">
                      <i className={tagIcon}></i>
                      {notice.tag || 'Circular'}
                    </span>
                    {fileUrl && (
                      <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="notice-card-dedicated__link">
                        Read Notice
                        <i className="fa-solid fa-arrow-right"></i>
                      </a>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
